import './StoriesList.sass';

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { HorizontalScroll } from '$uikit';
import { UnitName, UnitPhoto } from '$shared/components';
import {
  StoriesListItemProps,
  StreamListItemProps,
  SuggestionListItemProps,
} from './StoriesList.interface';
import {
  useCurrentUserId,
  useCurrentUserInfo,
  useDesktop,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { classNames as cn } from '$utils';
import { IcPlus28 } from '$assets';
import { useDispatch, useSelector } from 'react-redux';
import { storiesActions, storiesSelector } from '$store/stories';
import { goTo, showModal } from '$navigation/helper';
import { contentPrioritySelector } from '$store/main';
import { ContentPriorityType } from '$store/main/interface';
import { liveStreamsSelector } from '$store/live';
import { usersSuggestionsSelector } from '$store/feed';
import { PROFILE, RESOLVE } from '$shared/constants/pages';

export const StoriesListItem: FC<StoriesListItemProps> = (props) => {
  const { userId, stories } = props;
  const isDesktop = useDesktop();

  const user = useUserInfo(userId);

  const firstStoryId = stories[0].id;
  const hasUnreadStory = stories.some((story) => story.unread);
  const firstUnreadStoryId = stories.find((story) => story.unread)?.id;
  const storyId = firstUnreadStoryId || firstStoryId;

  const handleClick = useCallback(
    (e) => {
      showModal(
        `story${user.id}_${storyId}`,
        true,
        {},
        {
          unreadOnlyMode: hasUnreadStory,
        },
      );
    },
    [user, isDesktop, storyId, firstStoryId, hasUnreadStory],
  );

  return (
    <div
      className={cn({
        StoriesListItem: true,
        seen: !hasUnreadStory,
      })}
      id={`story_${userId}`}
      onClick={handleClick}
    >
      <div className="StoriesListItem__photo_wrap">
        <UnitPhoto
          unit={user}
          routeName={null}
          nftIndicator={false}
          hasStory={true}
          size="story"
          round
          onlineIndicator={false}
        />
      </div>
      <div className="StoriesListItem__name">
        <UnitName unit={user} />
      </div>
    </div>
  );
};

export const StreamListItem: FC<StreamListItemProps> = (props) => {
  const { stream } = props;
  const currentUserId = useCurrentUserId();
  const user = useUserInfo(stream.streamerId);

  const handleClick = useCallback(
    (e) => {
      showModal(currentUserId === stream.streamerId ? `live` : `live${stream.id}`);
    },
    [user, currentUserId],
  );

  return (
    <div className="StreamListItem" onClick={handleClick}>
      <div className="StoriesListItem__photo_wrap">
        <UnitPhoto
          unit={user}
          routeName={null}
          nftIndicator={false}
          hasLive={true}
          size="story"
          round
          onlineIndicator={false}
        />
      </div>
      <div className="StoriesListItem__name">
        <UnitName unit={user} />
      </div>
    </div>
  );
};

export const SuggestionListItem: FC<SuggestionListItemProps> = (props) => {
  const { userId } = props;

  const user = useUserInfo(userId);

  const handleClick = useCallback(() => {
    goTo(
      user.domain ? RESOLVE : PROFILE,
      user.domain
        ? {
            domain: user.domain,
          }
        : {
            id: user.id,
          },
    );
  }, [user]);

  return (
    <div className="StreamListItem" onClick={handleClick}>
      <div className="StoriesListItem__photo_wrap">
        <UnitPhoto unit={user} routeName={null} nftIndicator={false} size="story" />
      </div>
      <div className="StoriesListItem__name">
        <UnitName unit={user} />
      </div>
    </div>
  );
};

export const StoriesCreateItem: FC = (props) => {
  const [mediaInputKey, setMediaInputKey] = useState(0);
  const dispatch = useDispatch();
  const t = useTranslator();

  const handleMediaChange = useCallback(
    ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      dispatch(storiesActions.uploadStory({ file: files[0] }));
      setMediaInputKey(Date.now());
    },
    [dispatch],
  );

  return (
    <div className="StoriesNewItem">
      <div className="StoriesListItem__circle">
        <IcPlus28 />
        <input
          key={mediaInputKey}
          type="file"
          accept="image/*,video/*"
          onChange={handleMediaChange}
        />
      </div>
      <div className="StoriesListItem__name">{t('stories_list_new_story')}</div>
    </div>
  );
};

export const StoriesList: FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { stories, isLoading } = useSelector(storiesSelector);
  const streams = useSelector(liveStreamsSelector);
  const suggestions = useSelector(usersSuggestionsSelector);
  const currUser = useCurrentUserInfo();
  const contentPriority = useSelector(contentPrioritySelector);
  const isAdult = contentPriority === ContentPriorityType.ADULT;

  const handleScrollTo = useCallback(
    (e: any) => {
      if (!scrollRef.current) {
        return;
      }

      const userId = e.detail;
      const el = document.querySelector(`#story_${userId}`) as HTMLDivElement;
      if (el) {
        scrollRef.current.scrollLeft =
          el.offsetLeft - scrollRef.current.offsetWidth / 2 + el.offsetWidth / 2;
      }
    },
    [scrollRef],
  );

  useEffect(() => {
    addEventListener('storiesListScrollToUser', handleScrollTo);

    return () => {
      removeEventListener('storiesListScrollToUser', handleScrollTo);
    };
  }, []);

  if (
    isAdult &&
    currUser.verified !== 2 &&
    !stories.length &&
    !streams.length &&
    !suggestions.length
  ) {
    return null;
  }

  return (
    <div className="StoriesList">
      <HorizontalScroll ref={scrollRef}>
        <div className="StoriesList__content">
          {(!isAdult || currUser.verified === 2) && <StoriesCreateItem />}
          {streams.map((stream, i) => (
            <StreamListItem key={stream.id} stream={stream} />
          ))}
          {stories.map((item, i) => (
            <StoriesListItem key={i} userId={item.userId} stories={item.stories} />
          ))}
          {suggestions.map((userId, i) => (
            <SuggestionListItem key={i} userId={userId} />
          ))}
        </div>
      </HorizontalScroll>
    </div>
  );
};
